'use client'

import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { db } from '../lib/firebase.js';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ArrowLeft, Share2 } from 'lucide-react';
import ShareDialog from '../components/ShareDialog/ShareDialog.js';
import StoryAudioPlayer from '../components/AudioPlayer/StoryAudioPlayer.js';
import { Storybook3DReader } from '../components/Storybook/index.js';
import { Helmet } from 'react-helmet-async';
import { toUserSafeError } from '../utils/storyState.js';
import { Alert, ErrorState, LoadingState } from '../components/ui/feedback.jsx';
import { Button } from '../components/ui/button.jsx';

function StoryDisplayPage() {
  const { user, isLoaded: isUserLoaded } = useUser();
  const { getToken } = useAuth();
  const { storyId } = useParams();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [retryingImages, setRetryingImages] = useState(false);
  const [retryProgress, setRetryProgress] = useState({ completed: 0, total: 0 });

  useEffect(() => {
    const fetchStory = async () => {
      if (!isUserLoaded) return;
      if (!storyId) {
        setError('No story ID provided.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const storyRef = doc(db, 'stories', storyId);
        const storySnap = await getDoc(storyRef);

        if (!storySnap.exists()) {
          setError('Story not found.');
          return;
        }

        const data = { id: storySnap.id, ...storySnap.data() };

        const isOwner = Boolean(user?.id && data.user_id === user.id);
        if (data.is_public !== true && !isOwner) {
          setError('This story is private. Ask the owner to share it with you.');
          return;
        }

        if (!data.story_content) {
          setError('Story has no content.');
          return;
        }

        setStory(data);
    } catch (err) {
        setError(toUserSafeError(err, 'Failed to load this story. Please try again.'));
      } finally {
        setLoading(false);
      }
    };

    fetchStory();
  }, [storyId, user?.id, isUserLoaded]);

  const handleOpenShareDialog = async () => {
    if (!isOwnStory) {
      setShareDialogOpen(true);
      return;
    }

    try {
      if (story?.is_public !== true) {
        await updateDoc(doc(db, 'stories', storyId), { is_public: true });
        setStory((prev) => (prev ? { ...prev, is_public: true } : prev));
      }
      setShareDialogOpen(true);
    } catch (err) {
      setError('Could not enable sharing for this story. Please try again.');
    }
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
  };

  const isOwnStory = !!(story?.user_id && user?.id && story.user_id === user.id);
  const authorName = story?.author_name
    || (isOwnStory && (user?.fullName || user?.username))
    || 'Make It Mindful';

  const handleAudioStored = async (voice, downloadUrl) => {
    if (!storyId || !voice || !downloadUrl) return;
    const nextAudioUrls = { ...(story?.audio_urls || {}), [voice]: downloadUrl };
    setStory((prev) => (prev ? { ...prev, audio_urls: nextAudioUrls } : prev));
    if (isOwnStory) {
      try {
        const token = await getToken();
        const response = await fetch('/api/update-story-audio', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ storyId, voice, downloadUrl }),
        });
        if (!response.ok) throw new Error('Audio metadata update failed.');
        await updateDoc(doc(db, 'stories', storyId), { audio_status: 'completed', updated_at: new Date().toISOString() });
      } catch (err) {
        console.warn('Failed to persist audio URL on story doc:', err.message);
      }
    }
  };

  const handlePageAudioStored = async (voice, pageKey, downloadUrl) => {
    if (!storyId || !voice || !pageKey || !downloadUrl) return;
    const currentPageAudioUrls = story?.audio_page_urls || {};
    const nextVoicePages = { ...(currentPageAudioUrls[voice] || {}), [pageKey]: downloadUrl };
    const nextPageAudioUrls = { ...currentPageAudioUrls, [voice]: nextVoicePages };
    setStory((prev) => (prev ? { ...prev, audio_page_urls: nextPageAudioUrls } : prev));
    if (isOwnStory) {
      try {
        const token = await getToken();
        const response = await fetch('/api/update-story-audio', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ storyId, voice, pageKey, downloadUrl }),
        });
        if (!response.ok) throw new Error('Page audio metadata update failed.');
        await updateDoc(doc(db, 'stories', storyId), { audio_status: 'partial', updated_at: new Date().toISOString() });
      } catch (err) {
        console.warn('Failed to persist page audio URL on story doc:', err.message);
      }
    }
  };

  const handleRetryImages = async () => {
    if (!isOwnStory || !story?.pages?.length) return;
    const pageNumbers = story.pages
      .filter((page) => !page?.imageUrl && page?.imageStatus !== 'completed')
      .map((page) => Number(page.pageNumber))
      .filter(Boolean);
    if (!pageNumbers.length) return;
    setRetryingImages(true);
    setRetryProgress({ completed: 0, total: pageNumbers.length });
    setError(null);
    const failures = [];
    try {
      const token = await getToken();
      for (let index = 0; index < pageNumbers.length; index += 1) {
        const pageNumber = pageNumbers[index];
        try {
          const response = await fetch('/api/retry-story-images', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ storyId, pageNumbers: [pageNumber] }),
          });
          const result = await response.json().catch(() => ({}));
          // A concurrent retry may have claimed this page already. It is safe
          // to skip it because that request owns the generation attempt.
          if (!response.ok && response.status !== 409) {
            failures.push(result.error || (response.status === 504
              ? `Page ${pageNumber} timed out.`
              : `Page ${pageNumber} could not be illustrated.`));
          }
        } catch (retryError) {
          failures.push(retryError?.message || `Page ${pageNumber} could not be illustrated.`);
        } finally {
          setRetryProgress({ completed: index + 1, total: pageNumbers.length });
        }
      }
      const refreshed = await getDoc(doc(db, 'stories', storyId));
      if (refreshed.exists()) setStory({ id: refreshed.id, ...refreshed.data() });
      if (failures.length) {
        setError(`${failures.length} illustration${failures.length === 1 ? '' : 's'} still need attention. You can retry them again.`);
      }
    } catch (err) {
      setError(toUserSafeError(err, 'Illustrations could not be retried yet.'));
    } finally {
      setRetryingImages(false);
      setRetryProgress({ completed: 0, total: 0 });
    }
  };

  const selectedVoice = story?.selected_voice || 'alloy';
  const storedAudioUrl = story?.audio_urls?.[selectedVoice];

  const pageTitle = story?.title || 'Make It Mindful Story';
  const ogDescription = `Read "${story?.title || 'Untitled Story'}" - a personalized children's story created with Make It Mindful.`;

  if (loading) {
    return <LoadingState message="Opening your story…" />;
  }

  if (error && !story) {
    return <div className="mx-auto max-w-xl px-4 py-12"><ErrorState message={error} /></div>;
  }

  if (!story) {
    return <div className="mx-auto max-w-xl px-4 py-12"><ErrorState title="Story not found" message="This story may have been removed or is no longer available." /></div>;
  }

  // Check if story has pages for storybook view
  const hasPages = story?.pages && Array.isArray(story.pages) && story.pages.length > 0;
  const hasStoredPageImages = hasPages && story.pages.some((page) => Boolean(page?.imageUrl || page?.image_url || page?.image));
  const illustrationsEnabled = story?.image_model
    ? story.image_model !== 'none' && (
      story.images_status === 'completed' ||
      story.images_status === 'generating' ||
      hasStoredPageImages
    )
    : story?.images_status
      ? story.images_status === 'completed' || story.images_status === 'generating' || hasStoredPageImages
      : hasStoredPageImages;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta property="og:title" content={story.title || 'Untitled Story'} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:url" content={window.location.href} />
        {story.cover_image_url && (
          <meta property="og:image" content={story.cover_image_url} />
        )}
      </Helmet>

      <div className={`mx-auto w-full px-3 py-4 sm:px-5 sm:py-8 ${hasPages ? 'max-w-6xl' : 'max-w-2xl'}`}>
        {/* Header with navigation and share */}
        <div className="mb-4 flex items-center justify-between gap-3 px-1">
          <RouterLink to="/library" className="inline-flex min-h-11 items-center gap-2 rounded-md px-3 text-sm font-semibold text-primary transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to Library
          </RouterLink>
          <div className="flex items-center gap-2">
            {story.created_at && (
              <span className="hidden text-xs text-muted-foreground sm:block">
                Created: {new Date(story.created_at).toLocaleDateString()}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={handleOpenShareDialog}>
              <Share2 className="mr-2 h-4 w-4" aria-hidden="true" />
              Share
            </Button>
          </div>
        </div>

        {/* Storybook Viewer or Fallback */}
        {(story.images_status === 'failed' || story.images_status === 'partial') && (
          <Alert variant="warning" title="Some illustrations need attention" className="mb-4">
            <p>Your story text is safe, so you can read now. Missing illustrations will be generated one page at a time.</p>
            {error && <p className="mt-2 text-sm text-rose-800" role="alert">{error}</p>}
            {isOwnStory && <Button variant="outline" size="sm" className="mt-3" onClick={handleRetryImages} disabled={retryingImages}>
              {retryingImages
                ? `Painting ${retryProgress.completed} of ${retryProgress.total}…`
                : 'Retry illustrations'}
            </Button>}
          </Alert>
        )}
        {hasPages ? (
          <Storybook3DReader
            storyId={storyId}
            title={story.title || 'Untitled Story'}
            author={authorName}
            pages={story.pages}
            coverImageUrl={story.cover_image_url}
            illustrationsEnabled={illustrationsEnabled}
            showControls={true}
            narration={{
              mode: 'tts',
              voice: selectedVoice,
              storyId,
              pageAudioUrls: story.audio_page_urls?.[selectedVoice] || {},
              onPageAudioStored: handlePageAudioStored,
            }}
          />
        ) : (
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-[var(--shadow-card)]">
            <div className="p-4 sm:p-6">
              <h1 className="text-center font-display text-2xl font-bold text-foreground">
                {story.title || 'Untitled Story'}
              </h1>

              {story?.story_content && (
                <div className="mb-2">
                  <StoryAudioPlayer
                    storyText={story.story_content}
                    voice={selectedVoice}
                    storyId={storyId}
                    storedAudioUrl={storedAudioUrl}
                    onAudioStored={handleAudioStored}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-6 flex justify-center">
          <RouterLink to="/wizard" className="inline-flex min-h-11 items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-bold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            Create Your Own Story
          </RouterLink>
        </div>

        <ShareDialog
          open={shareDialogOpen}
          onClose={handleCloseShareDialog}
          title={story.title || 'Shared Story'}
          storyUrl={window.location.href}
          isOwnStory={isOwnStory}
        />
      </div>
    </>
  );
}

export default StoryDisplayPage;
